import {
  agentAppEventSchema,
  describeAgentAppEventAction,
  type AgentActor,
  type AgentAppEvent,
} from "@opencloud/agent-protocol";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  DatabaseBackup,
  Plug,
  Rocket,
  RotateCw,
  Settings2,
  Undo2,
  Unplug,
  UserMinus,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { agentBrand } from "../brand.js";

const icons: Record<AgentAppEvent["kind"], LucideIcon> = {
  release: Rocket,
  rollback: Undo2,
  backup_restore: DatabaseBackup,
  archive: Archive,
  unarchive: ArchiveRestore,
  restart: RotateCw,
  settings: Settings2,
  audiences: Users,
  access_granted: UserPlus,
  access_removed: UserMinus,
  integration_connected: Plug,
  integration_disconnected: Unplug,
};

/**
 * A retained event this bundle understands. A newer kind from the server falls
 * back to the stored sentence instead of an incomplete row.
 */
export function knownAppEvent(value: unknown): AgentAppEvent | null {
  if (value === undefined) return null;
  const parsed = agentAppEventSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Who made a change outside the conversation, from the viewer's perspective. */
export function appEventActorLabel(
  event: AgentAppEvent,
  actor: AgentActor | undefined,
  self: boolean,
): string {
  if (self) return "You";
  if (actor) return actor.name || "Collaborator";
  return event.channel === "platform"
    ? `${agentBrand().productName} administrator`
    : "Someone with app access";
}

/**
 * One compact line for an app change made outside this conversation (CLI,
 * MCP, web or platform administration). It records what happened; current
 * deployment truth stays beside the app title.
 */
export function AppEventRow({
  event,
  actor,
  self,
  messageId,
  focused,
  timeLabel,
}: {
  event: AgentAppEvent;
  actor: AgentActor | undefined;
  self: boolean;
  messageId: string;
  focused: boolean;
  timeLabel: string;
}) {
  const failed = event.outcome === "failed";
  const Icon = failed ? AlertTriangle : icons[event.kind];
  return (
    <div
      id={`message-${messageId}`}
      data-message-id={messageId}
      data-search-focus={focused || undefined}
      data-app-event={event.kind}
      data-outcome={event.outcome}
      className="a3-app-event"
      role="note"
    >
      <Icon aria-hidden="true" />
      {!self && actor?.avatarUrl ? (
        <img src={actor.avatarUrl} alt="" width={20} height={20} />
      ) : null}
      <span className="a3-app-event-text">
        <strong>{appEventActorLabel(event, actor, self)}</strong>{" "}
        {describeAgentAppEventAction(event)}
      </span>
      {timeLabel ? <time dateTime={event.occurredAt}>{timeLabel}</time> : null}
    </div>
  );
}
